import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';

import { api } from '@/api';
import { API_URL } from '@/dtos';
import { Actions } from '@/modules/ActivityLog/LogList/useGetActivityLogs.query';

export const GET_CONTRIBUTION_CHANGESETS_KEY = 'GET_CONTRIBUTION_CHANGESETS_KEY';

interface SortPayload {
  empty?: string;
  unsorted?: string;
  sorted?: string;
}

interface PageablePayload {
  offset?: string;
  sort?: SortPayload;
  unpaged?: string;
  pageNumber?: string;
  pageSize?: string;
  paged?: string;
}

interface ChangesetPayload {
  id?: string;
  changed_by_name?: string;
  changed_at?: string;
  action_type?: Actions;
  changes_count?: number;
  updated_employees_number?: number;
}

interface ContributionChangesetsResponsePayload {
  totalPages?: string;
  totalElements?: string;
  size?: string;
  content?: ChangesetPayload[];
  number?: string;
  sort?: SortPayload;
  pageable?: PageablePayload;
  first?: string;
  last?: string;
  numberOfElements?: string;
  empty?: string;
}

export interface Changeset {
  id?: string;
  changedByName?: string;
  changedAt?: string;
  actionType?: Actions;
  changesCount?: number;
  updatedEmployeesNumber?: number;
}

interface ContributionChangesetsResponse {
  totalPages?: number;
  totalElements?: number;
  size?: number;
  content?: Changeset[];
  number?: number;
  first?: boolean;
  last?: boolean;
  numberOfElements?: number;
  empty?: boolean;
}

interface QueryParams {
  page?: number;
  size?: number;
  sort?: string;
}

export const useGetContributionChangesets = (requestId?: string, params?: QueryParams) => {
  const { data, isLoading, refetch } = useQuery(
    [GET_CONTRIBUTION_CHANGESETS_KEY, requestId, params],
    () =>
      // TODO: this type is missing
      api.get<ContributionChangesetsResponsePayload>(
        API_URL.AUDIT_CONTRIBUTION_REQUESTS_CHANGESETS_BY_REQUEST_ID(requestId || ''),
        {
          params: {
            ...(params?.page ? { page: params.page - 1 } : {}),
            ...(params?.size ? { size: params?.size } : {}),
            ...(params?.sort ? { sort: params.sort } : {}),
          },
        }
      ),
    {
      enabled: Boolean(requestId),
    }
  );

  const formattedData = useMemo<ContributionChangesetsResponse>(() => {
    const payload = data?.data;

    return {
      totalPages: payload?.totalPages ? parseInt(payload.totalPages, 10) : undefined,
      totalElements: payload?.totalElements ? parseInt(payload.totalElements, 10) : undefined,
      size: payload?.size ? parseInt(payload.size, 10) : undefined,
      content: payload?.content?.map((item) => ({
        id: item?.id,
        changedByName: item?.changed_by_name,
        changedAt: item?.changed_at,
        actionType: item?.action_type,
        changesCount: item.changes_count,
        updatedEmployeesNumber: item?.updated_employees_number,
      })),
      number: payload?.number ? parseInt(payload.number, 10) : undefined,
      first: payload?.first === 'true',
      last: payload?.last === 'true',
      numberOfElements: payload?.numberOfElements ? parseInt(payload.numberOfElements, 10) : undefined,
      empty: payload?.empty === 'true',
    };
  }, [data]);

  return {
    formattedData,
    isLoading,
    refetch,
  };
};
